import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiError, resolveImageUrl } from '../../lib/api';

const ProjectsList = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/projects');
      setProjects(data);
      setError('');
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (slug) => {
    try {
      await api.delete(`/admin/projects/${encodeURIComponent(slug)}`);
      setConfirming(null);
      await load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-projects-list">
      <div className="flex items-start justify-between mb-8 gap-4">
        <div>
          <h1 className="text-4xl font-light">Projets</h1>
          <p className="text-gray-500 font-light mt-1">
            {loading ? 'Chargement...' : `${projects.length} projet(s)`}
          </p>
        </div>
        <Link
          to="/admin/projects/new"
          className="text-sm font-light tracking-wider uppercase bg-black text-white px-5 py-3 hover:bg-[#d94e47] transition-colors"
          data-testid="admin-new-project"
        >
          + Nouveau projet
        </Link>
      </div>

      {error && (
        <p className="text-[#d94e47] font-light mb-4" data-testid="projects-error">{error}</p>
      )}

      <div className="bg-white border border-gray-200">
        <div className="grid grid-cols-[80px_1.5fr_1fr_1fr_auto] gap-4 px-6 py-3 text-xs font-light tracking-wider uppercase text-gray-500 border-b border-gray-200">
          <span></span>
          <span>Titre</span>
          <span>Catégorie</span>
          <span>Lieu</span>
          <span className="text-right">Actions</span>
        </div>
        {projects.map((p) => (
          <div
            key={p.slug}
            className="grid grid-cols-[80px_1.5fr_1fr_1fr_auto] gap-4 px-6 py-4 border-b border-gray-100 items-center"
            data-testid={`admin-project-row-${p.slug}`}
          >
            <div className="w-[64px] h-[48px] bg-gray-100 overflow-hidden">
              {p.images?.[0]?.url && (
                <img
                  src={resolveImageUrl(p.images[0].url)}
                  alt=""
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            <div className="font-light">
              <p className="text-sm">{p.title}</p>
              <p className="text-xs text-gray-400">{p.slug}</p>
            </div>
            <p className="text-sm font-light text-gray-600">{p.category}</p>
            <p className="text-sm font-light text-gray-600">{p.location}</p>
            <div className="flex gap-4 justify-end text-xs font-light tracking-wider uppercase">
              <Link
                to={`/admin/projects/${encodeURIComponent(p.slug)}`}
                className="text-black hover:text-[#d94e47]"
                data-testid={`edit-project-${p.slug}`}
              >
                Modifier
              </Link>
              {confirming === p.slug ? (
                <>
                  <button
                    type="button"
                    onClick={() => onDelete(p.slug)}
                    className="text-[#d94e47] hover:underline"
                    data-testid={`confirm-delete-project-${p.slug}`}
                  >
                    Confirmer
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirming(null)}
                    className="text-gray-400 hover:underline"
                  >
                    Annuler
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setConfirming(p.slug)}
                  className="text-gray-500 hover:text-[#d94e47]"
                  data-testid={`delete-project-${p.slug}`}
                >
                  Supprimer
                </button>
              )}
            </div>
          </div>
        ))}
        {!loading && projects.length === 0 && (
          <p className="text-center text-gray-500 font-light py-12">Aucun projet.</p>
        )}
      </div>
    </div>
  );
};

export default ProjectsList;
